import { Browser } from "@capacitor/browser";
import { completePhoneVerification } from "@/app/actions/verification";
import { logNativeFlow } from "@/lib/native-flow-diagnostics";
import { consumeIdentityReturnPath, takeIdentityResultHandler } from "@/lib/auth/identity-bridge";

/**
 * Capacitor 본인인증 브릿지 deep link 핸들러.
 *
 * 포맷: locarora://identity-complete?identityVerificationId=...
 *                                  &code=...&message=...&pgCode=...&pgMessage=...
 *
 * useKoreanPhoneVerification 이 /<locale>/verify-phone-external 을 연다 (iOS 는
 * 인앱 브라우저, 그 외는 외부 브라우저). 인증이 끝나면 PortOne 이
 * /<locale>/identity-bridge-callback 으로 보내고, 그 페이지가 위 deep link 를 발사 →
 * appUrlOpen 리스너(`CapacitorBootstrap`)가 이 함수를 호출.
 *
 * 처리:
 *   1. Browser.close — 인앱 브라우저를 닫는다 (외부 브라우저로 열었으면 no-op).
 *   2. 시작 화면이 살아 있으면(핸들러 있음) 결과를 그 화면에 전달한다. 이동하지 않으므로
 *      결제 입력값이 유지된다.
 *   3. 화면이 사라졌으면 서버 확인 후 시작 화면 경로로, 경로도 없으면 홈으로 이동한다.
 *
 * 반환: 이 deep link 가 우리 책임이면 true.
 */

/** 인증 페이지에서 시작했으면 그 페이지가 가리키던 returnTo 로 간다. */
function destinationAfterSuccess(returnPath: string | null, locale: string): string {
  const home = `/${locale}`;
  if (!returnPath) return home;
  const url = new URL(returnPath, "https://locarora.invalid");
  if (!url.pathname.endsWith("/verify-phone")) return returnPath;
  const returnTo = url.searchParams.get("returnTo");
  return returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : home;
}

function verifyPhoneErrorUrl(locale: string, error: string, returnPath: string | null): string {
  const destination = destinationAfterSuccess(returnPath, locale);
  const returnTo = destination === `/${locale}` ? "" : `&returnTo=${encodeURIComponent(destination)}`;
  return `/${locale}/verify-phone?error=${encodeURIComponent(error)}${returnTo}`;
}

/**
 * NOTE: Deep link dedup 은 CapacitorBootstrap dispatch level 에서 중앙
 * 처리한다 (`@/lib/auth/deep-link-dedup`). 같은 패턴이 oauth / payment /
 * identity 핸들러 모두에 필요하므로 한 곳에서 막아주는 게 일관적임.
 */

export async function processIdentityDeepLink(
  urlStr: string,
): Promise<boolean> {
  let url: URL;
  try {
    url = new URL(urlStr);
  } catch {
    return false;
  }

  if (url.protocol !== "locarora:" || url.host !== "identity-complete") {
    return false;
  }

  const params = url.searchParams;
  const identityVerificationId = params.get("identityVerificationId") ?? "";
  const code = params.get("code");
  const message = params.get("message");
  const pgCode = params.get("pgCode");
  const pgMessage = params.get("pgMessage");
  // 사용자가 verify-phone 단계에서 입력한 phone — bridge page 가 callback URL
  // 에 실어 보낸다. PortOne API 응답의 verifiedCustomer.phoneNumber 가 빈 경우
  // server action fallback 으로 쓰임.
  const userInputPhone = params.get("phone") || undefined;

  logNativeFlow("identity-deep-link", "received", {
    identityVerificationId,
    code,
    message,
    pgCode,
    pgMessage,
  });

  const handler = takeIdentityResultHandler();
  const returnPath = consumeIdentityReturnPath();
  // 창이 닫히는 순간 버튼이 다시 눌리지 않게, 닫기 전에 처리 중으로 표시한다.
  if (!code && identityVerificationId) handler?.onProcessing();

  // SFSafariViewController 닫기
  try {
    await Browser.close();
  } catch {
    // 이미 닫혔거나 Android에서 실패해도 UX 영향 없음
  }

  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const locale = pathSegments[0] || "en";

  if (code) {
    if (code === "USER_CANCEL") {
      // 사용자 취소 — 현재 페이지 유지, silent
      logNativeFlow("identity-deep-link", "user-cancelled", {
        identityVerificationId,
      });
      handler?.onCancelled();
      return true;
    }
    logNativeFlow(
      "identity-deep-link",
      "error",
      { identityVerificationId, code, message },
      "warn",
    );
    if (handler) {
      handler.onFailed(code);
      return true;
    }
    window.location.href = verifyPhoneErrorUrl(locale, message || code, returnPath);
    return true;
  }

  if (!identityVerificationId) {
    logNativeFlow(
      "identity-deep-link",
      "missing-id",
      {},
      "warn",
    );
    if (handler) {
      handler.onFailed("UNKNOWN_ERROR");
      return true;
    }
    window.location.href = `/${locale}/verify-phone`;
    return true;
  }

  // 서버측 검증 + customer_profiles 업데이트
  try {
    logNativeFlow("identity-deep-link", "complete-verification.start", {
      identityVerificationId,
    });
    const result = await completePhoneVerification(
      identityVerificationId,
      "danal",
      "KR",
      userInputPhone,
    );
    logNativeFlow(
      "identity-deep-link",
      "complete-verification.done",
      {
        identityVerificationId,
        success: result.success,
        errorMessage: result.error,
      },
      result.success ? "info" : "warn",
    );

    if (handler) {
      handler.onVerified(result);
      return true;
    }

    if (!result.success) {
      window.location.href = verifyPhoneErrorUrl(locale, result.error || "UNKNOWN_ERROR", returnPath);
      return true;
    }

    window.location.href = destinationAfterSuccess(returnPath, locale);
    return true;
  } catch (err) {
    logNativeFlow(
      "identity-deep-link",
      "complete-verification.threw",
      {
        identityVerificationId,
        errorMessage: err instanceof Error ? err.message : String(err),
      },
      "error",
    );
    console.error("[identity-deep-link] complete failed", err);
    if (handler) {
      handler.onFailed("UNKNOWN_ERROR");
      return true;
    }
    window.location.href = verifyPhoneErrorUrl(locale, "processing_failed", returnPath);
    return true;
  }
}
